import { useEffect, useRef, useState } from 'react';

/**
 * HeroChat — teléfono interactivo del hero de la landing.
 * Bucle vivo de conversaciones (inmobiliaria → autos → colegio) donde el Agente IA
 * contesta, manda foto/video/nota de voz y agenda la cita. Portado del mockup aprobado.
 * Respeta prefers-reduced-motion (muestra una conversación estática).
 */

const SCRIPTS = [
  [ // inmobiliaria
    { who: 'me', t: 'Hola, vi el anuncio 👀 ¿me pasan info?', d: '11:47' },
    { typing: 1 }, { who: 'them', t: '¡Hola! Claro que sí 😊 ¿Para ti o para invertir?', d: '11:47' },
    { who: 'me', img: 'img1', ph: '📸', cap: 'Busco algo así 👇', d: '11:48' },
    { typing: 1 }, { who: 'them', t: '¡Justo tengo eso! Mira 👇', d: '11:48' },
    { who: 'them', img: 'img2', ph: '🏠', cap: '2 rec · $4.35M · entrega inmediata', d: '11:48' },
    { who: 'them', vid: 1, badge: '0:24', cap: 'Recorrido en video 🎥', d: '11:49' },
    { who: 'me', t: 'Me encanta 😍', d: '11:49' },
    { typing: 1 }, { who: 'them', voice: 1, vt: '0:14', d: '11:49' },
    { who: 'them', t: '¿Te agendo visita el sábado 12:00? 🗝️', d: '11:49' },
    { who: 'me', t: 'Sí ✅', d: '11:50' },
    { who: 'them', t: '¡Listo! Cita confirmada 🎉 Te llega recordatorio.', d: '11:50' },
  ],
  [ // autos
    { who: 'me', t: '¿Sigue disponible la camioneta? 🚙', d: '09:12' },
    { typing: 1 }, { who: 'them', t: '¡Sí! Te paso ficha y precio 👇', d: '09:12' },
    { who: 'them', img: 'img2', ph: '🚗', cap: 'SUV 2024 · $529,000 · 12,000 km', d: '09:12' },
    { who: 'them', t: 'Con enganche de $80k queda en $9,850/mes 💳', d: '09:13' },
    { who: 'me', t: '¿Puedo probarla?', d: '09:13' },
    { typing: 1 }, { who: 'them', vid: 1, badge: '0:18', cap: 'Mira el arranque 🎥', d: '09:13' },
    { who: 'them', t: '¿Te agendo prueba de manejo mañana 5pm? 🚗', d: '09:14' },
    { who: 'me', t: 'Va 👍', d: '09:14' },
    { who: 'them', t: '¡Agendada! Te espera un asesor 🎉', d: '09:14' },
  ],
  [ // colegio
    { who: 'me', t: 'Hola, informes de inscripción 🎓', d: '18:40' },
    { typing: 1 }, { who: 'them', t: '¡Con gusto! ¿Para qué grado? 😊', d: '18:40' },
    { who: 'me', t: 'Primaria', d: '18:41' },
    { typing: 1 }, { who: 'them', t: 'Colegiatura $4,200/mes · inscripción $6,500 📋', d: '18:41' },
    { who: 'them', img: 'img1', ph: '🏫', cap: 'Conoce el campus', d: '18:41' },
    { who: 'them', voice: 1, vt: '0:11', d: '18:42' },
    { who: 'them', t: '¿Te agendo un tour el viernes 10am? 🗓️', d: '18:42' },
    { who: 'me', t: 'Perfecto ✅', d: '18:42' },
    { who: 'them', t: '¡Listo! Tour agendado 🎉', d: '18:42' },
  ],
];

function Bubble({ m }) {
  if (m.typing) {
    return (
      <div className="self-start rounded-2xl rounded-bl-sm border border-white/10 bg-white/5 px-3.5 py-2.5 inline-flex gap-1 lc-pop">
        <i className="lc-dot" /><i className="lc-dot" style={{ animationDelay: '.2s' }} /><i className="lc-dot" style={{ animationDelay: '.4s' }} />
      </div>
    );
  }
  const mine = m.who === 'me';
  const base = 'max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-snug lc-pop';
  const side = mine
    ? 'self-end bg-lime-400/15 border border-lime-400/25 rounded-br-sm text-lime-50'
    : 'self-start bg-white/[0.06] border border-white/10 rounded-bl-sm text-white/90';
  if (m.img) {
    const g = m.img === 'img1' ? 'from-[#3a6fb0] to-[#7cc23e]' : 'from-[#274b86] to-[#4d7cc0]';
    return (
      <div className={`${side} !p-0 overflow-hidden max-w-[78%] lc-pop`}>
        <div className={`h-[110px] grid place-items-center text-3xl bg-gradient-to-br ${g}`}>{m.ph}</div>
        {m.cap && <div className="px-3 py-1.5 text-[12px] text-white/90">{m.cap}</div>}
        <span className="block px-3 pb-1.5 text-[9px] text-white/40 font-mono">{m.d}</span>
      </div>
    );
  }
  if (m.vid) {
    return (
      <div className={`${side} !p-0 overflow-hidden max-w-[78%] lc-pop`}>
        <div className="relative h-[110px] grid place-items-center bg-gradient-to-br from-[#1c3f74] to-[#0e2340]">
          <span className="w-11 h-11 rounded-full bg-white/90 grid place-items-center text-[#1c3f74] text-lg shadow-lg">▶</span>
          <span className="absolute bottom-1.5 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">{m.badge}</span>
        </div>
        {m.cap && <div className="px-3 py-1.5 text-[12px] text-white/90">{m.cap}</div>}
        <span className="block px-3 pb-1.5 text-[9px] text-white/40 font-mono">{m.d}</span>
      </div>
    );
  }
  if (m.voice) {
    return (
      <div className={`${side} lc-pop`}>
        <div className="flex items-center gap-2 min-w-[168px]">
          <span className="w-7 h-7 rounded-full bg-lime-400 text-[#0e2b06] grid place-items-center text-[11px] shrink-0">▶</span>
          <span className="flex items-center gap-[2px] h-5 flex-1">
            {Array.from({ length: 16 }).map((_, i) => (
              <i key={i} className="lc-wf" style={{ animationDelay: `${i * 0.06}s` }} />
            ))}
          </span>
          <span className="text-[10px] text-white/45 font-mono">{m.vt}</span>
        </div>
        <span className="block text-[9px] text-white/40 font-mono mt-1">nota de voz IA · {m.d}</span>
      </div>
    );
  }
  return (
    <div className={`${base} ${side}`}>
      {m.t}<span className="block text-[9px] text-white/40 font-mono mt-1">{m.d}</span>
    </div>
  );
}

export default function HeroChat() {
  const [msgs, setMsgs] = useState([]);
  const scrollRef = useRef(null);
  const reduce = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduce) { setMsgs(SCRIPTS[0].filter((m) => !m.typing)); return; }
    let si = 0, i = 0, alive = true;
    const timers = [];
    const wait = (ms, fn) => timers.push(setTimeout(() => { if (alive) fn(); }, ms));
    function step() {
      const script = SCRIPTS[si];
      if (i >= script.length) {
        wait(2400, () => { setMsgs([]); si = (si + 1) % SCRIPTS.length; i = 0; step(); });
        return;
      }
      const m = script[i];
      if (m.typing) {
        setMsgs((prev) => [...prev, { typing: true, _k: `t${si}-${i}` }]);
        wait(760, () => { setMsgs((prev) => prev.filter((x) => !x.typing)); i += 1; step(); });
      } else {
        setMsgs((prev) => [...prev, { ...m, _k: `${si}-${i}` }]);
        i += 1;
        wait(760, step);
      }
    }
    step();
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [reduce]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  return (
    <div className="relative mx-auto w-full max-w-[380px] rounded-[28px] border border-white/12 bg-gradient-to-b from-[#12233d] to-[#0b1526] p-3.5 shadow-2xl">
      <style>{`
        @keyframes lcpop{to{opacity:1;transform:none}}
        .lc-pop{animation:lcpop .38s cubic-bezier(.23,1,.32,1) forwards;opacity:0;transform:translateY(8px)}
        .lc-dot{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.45);display:inline-block;animation:lcblink 1.2s infinite}
        @keyframes lcblink{0%,60%,100%{opacity:.3;transform:translateY(0)}30%{opacity:1;transform:translateY(-3px)}}
        .lc-wf{display:block;width:2.5px;border-radius:2px;background:#a3e635;opacity:.8;animation:lcwf 1.1s cubic-bezier(.77,0,.175,1) infinite}
        @keyframes lcwf{0%,100%{height:5px}50%{height:17px}}
        @media (prefers-reduced-motion: reduce){.lc-pop{animation:none;opacity:1;transform:none}.lc-dot,.lc-wf{animation:none}}
      `}</style>
      <div className="flex items-center gap-2.5 px-1.5 pb-3 border-b border-white/10">
        <div className="w-9 h-9 rounded-full grid place-items-center text-[#08210a] font-extrabold text-sm bg-gradient-to-br from-[#7cc23e] to-[#4d7cc0]">◆</div>
        <div>
          <div className="text-[14px] font-semibold text-white leading-tight">LeadCEM · Asistente IA</div>
          <div className="text-[11px] text-lime-300 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-lime-400 shadow-[0_0_8px_#a3e635]" /> en línea · contesta en segundos
          </div>
        </div>
      </div>
      <div ref={scrollRef} className="flex flex-col gap-2 px-1 pt-3.5 pb-1.5 h-[380px] overflow-y-auto scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
        {msgs.map((m) => <Bubble key={m._k} m={m} />)}
      </div>
    </div>
  );
}
